import * as Collapsible from '@radix-ui/react-collapsible';
import { XCloseIcon } from '@vizit/icons';
import { cva, cx } from '@vizit/theme';
import * as React from 'react';
import { Button, ButtonLevel, ButtonSize } from '../button';
import { Text, TextSize, TextWeight } from '../text';
import { CollapsibleDrawerProvider, useCollapsibleDrawer } from './collapsible-drawer-provider';

// - Default mode: Uses fractional columns 1fr_1fr_1fr_1fr
// - Custom width mode: Uses fixed pixel columns 1fr_524px (xl > 1280px) and 1fr_366px (<1280px)
const rootVariants = cva({
  base: 'grid w-full h-full overflow-hidden transition-all ease-in-out duration-300 [@media(max-width:820px)]:max-h-screen',
  variants: {
    side: {
      left: '',
      right: '',
    },
    isOpen: {
      true: '',
      false: '',
    },
    hasCustomWidth: {
      true: '',
      false: '',
    },
  },
  compoundVariants: [
    // Default grid for standard drawer
    {
      isOpen: true,
      hasCustomWidth: false,
      className:
        'grid-cols-[1fr_1fr_1fr_1fr] max-lg:grid-cols-[1fr_1fr_1fr] [@media(max-width:820px)]:grid-rows-2 [@media(max-width:820px)]:grid-cols-1',
    },
    {
      isOpen: false,
      side: 'right',
      hasCustomWidth: false,
      className:
        'grid-cols-[1fr_1fr_1fr_0fr] max-lg:grid-cols-[1fr_1fr_0fr] [@media(max-width:820px)]:grid-rows-1 [@media(max-width:820px)]:grid-cols-1',
    },
    {
      isOpen: false,
      side: 'left',
      hasCustomWidth: false,
      className:
        'grid-cols-[0fr_1fr_1fr_1fr] max-lg:grid-cols-[0fr_1fr_1fr] [@media(max-width:820px)]:grid-rows-1 [@media(max-width:820px)]:grid-cols-1',
    },
    // Custom width grid for fixed-width drawer
    {
      isOpen: true,
      side: 'right',
      hasCustomWidth: true,
      className:
        'grid-cols-[1fr_524px] max-xl:grid-cols-[1fr_366px] [@media(max-width:820px)]:grid-rows-2 [@media(max-width:820px)]:grid-cols-1',
    },
    {
      isOpen: false,
      side: 'right',
      hasCustomWidth: true,
      className: 'grid-cols-[1fr_0px] [@media(max-width:820px)]:grid-rows-1 [@media(max-width:820px)]:grid-cols-1',
    },
    {
      isOpen: true,
      side: 'left',
      hasCustomWidth: true,
      className:
        'grid-cols-[524px_1fr] max-xl:grid-cols-[366px_1fr] [@media(max-width:820px)]:grid-rows-2 [@media(max-width:820px)]:grid-cols-1',
    },
    {
      isOpen: false,
      side: 'left',
      hasCustomWidth: true,
      className: 'grid-cols-[0px_1fr] [@media(max-width:820px)]:grid-rows-1 [@media(max-width:820px)]:grid-cols-1',
    },
  ],
});

const contentVariants = cva({
  base: 'bg-primary py-12 h-full max-h-screen overflow-y-auto data-[state=open]:animate-fade-in col-span-1 [@media(max-width:820px)]:row-span-1 [@media(max-width:820px)]:border-t-[1px] [@media(max-width:820px)]:border-t-gray-utility-200',
  variants: {
    side: {
      left: 'col-start-1 row-end-[none] border-r-[1px] border-r-gray-utility-200',
      right: 'border-l-[1px] border-l-gray-utility-200',
    },
  },
});

const triggerContainerVariants = cva({
  base: 'flex flex-col gap-4 w-full h-full min-h-0 flex-wrap [@media(max-width:820px)]:col-span-1 [@media(max-width:820px)]:row-span-1',
  variants: {
    side: {
      left: '',
      right: '',
    },
    isOpen: {
      true: '',
      false: '',
    },
    hasCustomWidth: {
      true: '',
      false: '',
    },
  },
  compoundVariants: [
    {
      hasCustomWidth: false,
      className: 'col-span-3 max-lg:col-span-2',
    },
    {
      isOpen: true,
      side: 'left',
      hasCustomWidth: false,
      className:
        'col-start-2 col-end-5 max-lg:col-start-2 max-lg:col-end-4 [@media(max-width:820px)]:col-start-1 [@media(max-width:820px)]:col-end-2',
    },
    {
      isOpen: false,
      side: 'left',
      hasCustomWidth: false,
      className:
        'col-start-1 col-end-5 max-lg:col-start-1 max-lg:col-end-4 [@media(max-width:820px)]:col-start-1 [@media(max-width:820px)]:col-end-2',
    },
    // Custom width trigger container (2-column grid)
    {
      hasCustomWidth: true,
      className: 'col-span-1',
    },
    {
      isOpen: true,
      side: 'left',
      hasCustomWidth: true,
      className: 'col-start-2 [@media(max-width:820px)]:col-start-1 [@media(max-width:820px)]:col-end-2',
    },
    {
      isOpen: false,
      side: 'left',
      hasCustomWidth: true,
      className: 'col-start-1 col-end-3 [@media(max-width:820px)]:col-start-1 [@media(max-width:820px)]:col-end-2',
    },
  ],
});

interface CollapsibleDrawerProps extends React.HTMLAttributes<HTMLDivElement> {
  /**
   * The title of the drawer.
   */
  title: string;
  /**
   * Whether the drawer is open by default.
   */
  defaultOpen?: boolean;
  /**
   * Whether the drawer is open.
   */
  isOpen?: boolean;
  /**
   * Whether the drawer is disabled.
   */
  isDisabled?: boolean;
  /**
   * The side of the drawer.
   */
  side?: 'left' | 'right';
  /**
   * Custom width classes for the drawer content.
   */
  contentWidth?: string;
  /**
   * The function to call when the drawer is opened or closed.
   */
  onOpenChange?(open: boolean): void;
}

type CollapsibleDrawerTriggerProps = Omit<Collapsible.CollapsibleTriggerProps, 'asChild'>;
interface CollapsibleDrawerContentProps extends Collapsible.CollapsibleContentProps {
  /**
   * Custom header content to replace the default header.
   * If provided, the default title and close button will not be rendered.
   */
  headerContent?: React.ReactNode;
}

function CollapsibleDrawerTrigger(props: CollapsibleDrawerTriggerProps) {
  const { children, className, ...other } = props;

  const { disabled, open, onOpenChange } = useCollapsibleDrawer();

  const handleOnClick = React.useCallback(() => {
    onOpenChange?.(!open);
  }, [open, onOpenChange]);

  return (
    <Collapsible.Trigger disabled={disabled} asChild onClick={handleOnClick} {...other}>
      {children}
    </Collapsible.Trigger>
  );
}

function CollapsibleDrawerContent(props: CollapsibleDrawerContentProps) {
  const { children, className, headerContent, ...other } = props;

  const { disabled, side, title, onOpenChange } = useCollapsibleDrawer();

  const handleOnClose = () => onOpenChange?.(false);

  const defaultHeader = (
    <div className="flex items-center justify-between gap-2 px-12 pb-8">
      <Text weight={TextWeight.Semibold} size={TextSize.Lg}>
        {title}
      </Text>
      {!disabled && (
        <Button
          onPress={handleOnClose}
          aria-label="Close"
          size={ButtonSize.Sm}
          level={ButtonLevel.Ghost}
          iconStart={<XCloseIcon size={20} />}
        />
      )}
    </div>
  );

  return (
    <Collapsible.Content className={cx(contentVariants({ side }), 'w-full', className)} {...other}>
      <section aria-label={title}>
        {headerContent ?? defaultHeader}
        {children}
      </section>
    </Collapsible.Content>
  );
}

function CollapsibleDrawer(props: CollapsibleDrawerProps) {
  const {
    title,
    children: childrenProp,
    className,
    defaultOpen = false,
    isDisabled = false,
    isOpen: isOpenProp,
    side = 'right',
    contentWidth,
    onOpenChange,
    ...other
  } = props;

  const [uncontrolledOpen, setUncontrolledOpen] = React.useState(defaultOpen);

  const isControlled = React.useMemo(() => isOpenProp !== undefined, [isOpenProp]);
  const open = React.useMemo(
    () => (isControlled ? isOpenProp : uncontrolledOpen),
    [isControlled, isOpenProp, uncontrolledOpen],
  );

  const handleOpenChange = (open: boolean) => {
    if (isDisabled) return;

    if (!isControlled) {
      setUncontrolledOpen(open);
    }

    onOpenChange?.(open);
  };

  const children = React.Children.toArray(childrenProp).filter((child) => {
    return React.isValidElement(child) && child.type !== CollapsibleDrawerContent;
  });

  const sidebar = React.Children.toArray(childrenProp).find((child) => {
    return React.isValidElement(child) && child.type === CollapsibleDrawerContent;
  });

  const hasCustomWidth = !!contentWidth;

  return (
    <CollapsibleDrawerProvider
      value={{
        title,
        open,
        onOpenChange: handleOpenChange,
        disabled: isDisabled,
        side,
        contentWidth,
        ...other,
      }}
    >
      <Collapsible.Root
        open={open}
        onOpenChange={handleOpenChange}
        className={cx(rootVariants({ side, isOpen: open, hasCustomWidth }), className)}
        {...other}
      >
        <div className={triggerContainerVariants({ side, isOpen: open, hasCustomWidth })}>{children}</div>
        {sidebar}
      </Collapsible.Root>
    </CollapsibleDrawerProvider>
  );
}

export { CollapsibleDrawer, CollapsibleDrawerTrigger, CollapsibleDrawerContent };
export type { CollapsibleDrawerProps, CollapsibleDrawerTriggerProps, CollapsibleDrawerContentProps };
